import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "OcrLivenessVerificationReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    ocr_liveness_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        personal_information: false,
        facial_comparison_liveness: false,
        document_authentication: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    safe_ocr_liveness_data() {
      return this.ocr_liveness_data || {};
    },

    // Report metadata
    report_reference() {
      return `OCR-LIV-${Date.now().toString().slice(-8)}`;
    },

    generated_date() {
      return new Date().toLocaleDateString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },

    // OCR Data
    ocr_data() {
      return this.safe_ocr_liveness_data.ocr_data || {};
    },

    document_details() {
      return this.ocr_data.document_details || {};
    },

    document_images() {
      return this.ocr_data.document_images || {};
    },

    document_authenticity() {
      return this.ocr_data.document_authenticity || {};
    },

    document_type_info() {
      return this.ocr_data.document_type_info || {};
    },

    // Liveness Data
    liveness_data() {
      return this.safe_ocr_liveness_data.liveness_data || {};
    },

    liveness_results() {
      return this.liveness_data.results || {};
    },

    integrated_analysis() {
      return this.liveness_data.integrated_analysis || {};
    },

    liveness_summary() {
      return this.liveness_data.summary || {};
    },

    quality_assessment() {
      return this.liveness_data.quality_assessment || {};
    },

    screen_detection() {
      return this.liveness_data.screen_detection || {};
    },

    // Facial Comparison Data
    facial_comparison_data() {
      return this.safe_ocr_liveness_data.facial_comparison_data || {};
    },

    facial_interpretation() {
      // Support both legacy and new API formats
      // Legacy: facial_comparison_data.interpretation.{confidence, message, recommendation}
      // New: facial_comparison_data.{confidence_level, message, recommendation} directly
      if (this.facial_comparison_data.interpretation) {
        // Legacy format
        return this.facial_comparison_data.interpretation;
      } else {
        // New format - create compatible object
        return {
          confidence: this.facial_comparison_data.confidence_level || "N/A",
          message: this.facial_comparison_data.message || "N/A",
          recommendation: this.facial_comparison_data.recommendation || "N/A"
        };
      }
    },

    selfie_image_quality() {
      // Support both legacy and new API formats
      // Legacy: facial_comparison_data.selfieImageQuality.quality
      // New: facial_comparison_data.selfie_quality
      if (this.facial_comparison_data.selfieImageQuality) {
        return this.facial_comparison_data.selfieImageQuality;
      } else if (this.facial_comparison_data.selfie_quality) {
        // New format - wrap in compatible structure
        return {
          quality: this.facial_comparison_data.selfie_quality
        };
      }
      return {};
    },

    comparison_image_quality() {
      // Support both legacy and new API formats
      // Legacy: facial_comparison_data.comparisonImageQuality.quality
      // New: facial_comparison_data.document_quality
      if (this.facial_comparison_data.comparisonImageQuality) {
        return this.facial_comparison_data.comparisonImageQuality;
      } else if (this.facial_comparison_data.document_quality) {
        // New format - wrap in compatible structure
        return {
          quality: this.facial_comparison_data.document_quality
        };
      }
      return {};
    },

    // Personal Information
    full_name() {
      return this.document_details["Surname And Given Names"] || "N/A";
    },

    given_names() {
      return this.document_details["Given Names"] || "N/A";
    },

    surname() {
      return this.document_details["Surname"] || "N/A";
    },

    date_of_birth() {
      return this.document_details["Date of Birth"] || "N/A";
    },

    formatted_date_of_birth() {
      return this.formatDate(this.date_of_birth);
    },

    age() {
      return this.document_details["Age"] || "N/A";
    },

    sex() {
      return this.document_details["Sex"] || "N/A";
    },

    formatted_sex() {
      return this.sex === "M" ? "Male" : this.sex === "F" ? "Female" : this.sex;
    },

    personal_number() {
      return this.document_details["Personal Number"] || "N/A";
    },

    citizenship_status() {
      return this.document_details["Citizenship status"] || "N/A";
    },

    nationality() {
      return this.document_details["Nationality"] || "N/A";
    },

    place_of_birth() {
      return this.document_details["eID PlaceOfBirth Country"] || "N/A";
    },

    issuing_state() {
      return this.document_details["Issuing State Name"] || "N/A";
    },

    issuing_country() {
      return this.document_type_info.dCountryName || "N/A";
    },

    // Document Type Information
    document_description() {
      return this.document_type_info.dDescription || "N/A";
    },

    document_year() {
      return this.document_type_info.dYear || "N/A";
    },

    icao_code() {
      return this.document_type_info.ICAOCode || "N/A";
    },

    pages_count() {
      return this.document_authenticity.pagesCount || "N/A";
    },

    // Document Authentication
    overall_auth_status() {
      return this.document_authenticity.overallStatus || 0;
    },

    document_auth_text() {
      const status_map = {
        0: "Not Checked",
        1: "Passed",
        2: "Failed",
      };
      return status_map[this.overall_auth_status] || "Unknown";
    },

    document_auth_class() {
      const class_map = {
        0: "text-info",
        1: "text-success",
        2: "text-danger",
      };
      return class_map[this.overall_auth_status] || "text-muted";
    },

    // Liveness Status
    final_decision() {
      return (
        this.integrated_analysis.final_decision ||
        this.liveness_summary.final_decision ||
        "N/A"
      );
    },

    liveness_status_text() {
      return this.final_decision === "LIVE"
        ? "Live"
        : this.final_decision === "SPOOF"
        ? "Spoof"
        : "Unknown";
    },

    liveness_status_class() {
      return this.final_decision === "LIVE"
        ? "text-success"
        : this.final_decision === "SPOOF"
        ? "text-danger"
        : "text-warning";
    },

    // Facial Comparison
    facial_similarity() {
      // Support both legacy and new API formats
      // New: normalized_score (already as percentage 0-100)
      // Legacy: similarity (decimal 0-1)
      if (this.facial_comparison_data.normalized_score !== undefined) {
        // New format - normalized_score is already a percentage
        return this.facial_comparison_data.normalized_score;
      }
      // Legacy format - similarity is a decimal, convert to percentage
      return (this.facial_comparison_data.similarity || 0) * 100;
    },

    facial_similarity_rounded() {
      return this.facial_similarity.toFixed(3);
    },

    facial_similarity_percentage() {
      return this.facial_similarity.toFixed(1);
    },

    facial_confidence() {
      return this.facial_interpretation.confidence || "N/A";
    },

    facial_message() {
      return this.facial_interpretation.message || "N/A";
    },

    facial_recommendation() {
      return this.facial_interpretation.recommendation || "N/A";
    },

    facial_recommendation_class() {
      return this.facial_recommendation === "Accept match"
        ? "text-success"
        : "text-warning";
    },

    facial_match_interpretation() {
      return this.facial_interpretation.confidence || "N/A";
    },
    facial_match_class() {
      const confidence = (this.facial_confidence || "").toLowerCase();
      if (confidence === "high") return "text-success";
      if (confidence === "medium") return "text-warning";
      if (confidence === "low") return "text-danger";
      return "text-muted";
    },

    // Liveness Scores (rounded to 3 decimal places)
    adjusted_liveness_score_rounded() {
      const score =
        this.integrated_analysis.adjusted_liveness_score ||
        this.liveness_summary.adjusted_liveness_score ||
        0;
      return score.toFixed(3);
    },

    quality_overall_score_rounded() {
      const score = this.quality_assessment.overall_score || 0;
      return score.toFixed(3);
    },

    liveness_confidence_rounded() {
      const confidence =
        this.integrated_analysis.confidence ||
        this.liveness_summary.confidence ||
        0;
      return confidence.toFixed(3);
    },

    // Liveness Detection Methods
    detection_methods() {
      // Correctly access detection_methods from liveness_data.liveness_detection
      const livenessDetectionObj = this.liveness_data.liveness_detection || {};
      return livenessDetectionObj.detection_methods || {};
    },

    texture_analysis_score() {
      return (this.detection_methods.texture_analysis || 0).toFixed(3);
    },

    frequency_analysis_score() {
      return (this.detection_methods.frequency_analysis || 0).toFixed(3);
    },

    eye_blink_score() {
      return (this.detection_methods.eye_blink || 0).toFixed(3);
    },

    color_analysis_score() {
      return (this.detection_methods.color_analysis || 0).toFixed(3);
    },

    geometric_analysis_score() {
      return (this.detection_methods.geometric_analysis || 0).toFixed(3);
    },

    // Screen Detection
    is_screen_likely() {
      return this.screen_detection.is_screen_likely || false;
    },

    screen_detection_recommendations() {
      return this.screen_detection.recommendations || [];
    },

    // Image Quality
    selfie_quality_level() {
      return this.selfie_image_quality.quality?.qualityLevel || "N/A";
    },

    selfie_blur_score() {
      return this.selfie_image_quality.quality?.blurScore || "N/A";
    },

    comparison_quality_level() {
      return this.comparison_image_quality.quality?.qualityLevel || "N/A";
    },

    comparison_blur_score() {
      return this.comparison_image_quality.quality?.blurScore || "N/A";
    },

    // Images
    selfie_image() {
      return this.safe_ocr_liveness_data.selfie_image || null;
    },

    comparison_image() {
      return this.safe_ocr_liveness_data.comparison_image || null;
    },

    selfie_image_src() {
      if (!this.selfie_image) return null;
      if (this.selfie_image.startsWith("data:")) return this.selfie_image;
      return `data:image/jpeg;base64,${this.selfie_image}`;
    },

    comparison_image_src() {
      if (!this.comparison_image) return null;
      if (this.comparison_image.startsWith("data:"))
        return this.comparison_image;
      return `data:image/jpeg;base64,${this.comparison_image}`;
    },

    portrait_image() {
      const image = this.document_images["Portrait"] || null;
      if (!image || image === "BASE_64") return null;
      if (image.startsWith("data:")) return image;
      return `data:image/jpeg;base64,${image}`;
    },

    signature_image() {
      const image = this.document_images["Signature"] || null;
      if (!image || image === "BASE_64") return null;
      if (image.startsWith("data:")) return image;
      return `data:image/jpeg;base64,${image}`;
    },

    // Export validation
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  methods: {
    formatDate(date) {
      return format_date(date);
    },

    auth_check_text(checkType) {
      const value = this.document_authenticity[checkType];
      const status_map = {
        0: "Not Checked",
        1: "Passed",
        2: "Failed",
      };
      return status_map[value] || "Unknown";
    },

    auth_check_class(checkType) {
      const value = this.document_authenticity[checkType];
      const class_map = {
        0: "text-info",
        1: "text-success",
        2: "text-danger",
      };
      return class_map[value] || "text-muted";
    },

    show_export_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("show");
    },

    close_modal() {
      const modalId = `export-modal-${this.report_reference}`;
      $(`#${modalId}`).modal("hide");
    },

    async load_export_libraries() {
      // Load Papa Parse for CSV
      if (!this.libraries_loaded.papa_parse) {
        try {
          await this.load_script(
            "https://unpkg.com/papaparse@5.3.2/papaparse.min.js"
          );
          this.libraries_loaded.papa_parse = true;
        } catch (error) {
          console.error("Failed to load Papa Parse:", error);
        }
      }

      // Load SheetJS for Excel
      if (!this.libraries_loaded.sheetjs) {
        try {
          await this.load_script(
            "https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js"
          );
          this.libraries_loaded.sheetjs = true;
        } catch (error) {
          console.error("Failed to load SheetJS:", error);
        }
      }
    },

    load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async export_data() {
      await this.load_export_libraries();

      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const fileName = `OCR_Liveness_Verification_${timestamp}`;

      if (this.export_format === "json") {
        this.export_json(fileName);
      } else if (this.export_format === "csv") {
        this.export_csv(fileName);
      } else if (this.export_format === "excel") {
        this.export_excel(fileName);
      }

      this.close_modal();
    },

    export_json(fileName) {
      const data = this.prepare_export_data();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      this.download_file(blob, `${fileName}.json`);
    },

    export_csv(fileName) {
      if (!window.Papa) {
        alert("CSV export library not loaded. Please try again.");
        return;
      }

      var Papa = window.Papa;

      const data = this.prepare_export_data();

      // Export each selected section as a separate CSV file
      Object.keys(data).forEach((section) => {
        if (Array.isArray(data[section]) && data[section].length > 0) {
          const csv = Papa.unparse(data[section]);
          const blob = new Blob([csv], { type: "text/csv" });
          this.download_file(blob, `${fileName}_${section}.csv`);
        } else if (typeof data[section] === "object") {
          // Convert object to array format for CSV
          const rows = Object.entries(data[section]).map(([key, value]) => ({
            Field: key,
            Value: value,
          }));
          const csv = Papa.unparse(rows);
          const blob = new Blob([csv], { type: "text/csv" });
          this.download_file(blob, `${fileName}_${section}.csv`);
        }
      });
    },

    export_excel(fileName) {
      if (!window.XLSX) {
        alert("Excel export library not loaded. Please try again.");
        return;
      }

      var XLSX = window.XLSX;

      const data = this.prepare_export_data();
      const workbook = XLSX.utils.book_new();

      Object.keys(data).forEach((section) => {
        let worksheet;
        if (Array.isArray(data[section]) && data[section].length > 0) {
          worksheet = XLSX.utils.json_to_sheet(data[section]);
        } else if (typeof data[section] === "object") {
          const rows = Object.entries(data[section]).map(([key, value]) => ({
            Field: key,
            Value: value,
          }));
          worksheet = XLSX.utils.json_to_sheet(rows);
        }

        if (worksheet) {
          XLSX.utils.book_append_sheet(workbook, worksheet, section);
        }
      });

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    },

    prepare_export_data() {
      const data = {};

      if (this.export_options.personal_information) {
        data.PersonalInformation = {
          "Full Name": this.full_name,
          "Given Names": this.given_names,
          Surname: this.surname,
          "Date of Birth": this.formatted_date_of_birth,
          Age: this.age,
          Sex: this.formatted_sex,
          "Personal Number": this.personal_number,
          "Citizenship Status": this.citizenship_status,
          Nationality: this.nationality,
          "Place of Birth": this.place_of_birth,
          "Issuing Country": this.issuing_country,
        };
      }

      if (this.export_options.facial_comparison_liveness) {
        data.FacialComparisonLiveness = {
          "Facial Similarity Score": this.facial_similarity_rounded,
          "Facial Confidence": this.facial_confidence,
          "Facial Message": this.facial_message,
          "Facial Recommendation": this.facial_recommendation,
          "Liveness Score": this.adjusted_liveness_score_rounded,
          "Quality Score": this.quality_overall_score_rounded,
          "Final Decision": this.final_decision,
          "Liveness Confidence": this.liveness_confidence_rounded,
          "Texture Analysis": this.texture_analysis_score,
          "Frequency Analysis": this.frequency_analysis_score,
          "Eye Blink": this.eye_blink_score,
          "Color Analysis": this.color_analysis_score,
          "Geometric Analysis": this.geometric_analysis_score,
          "Screen Detection": this.is_screen_likely ? "Yes" : "No",
          "Selfie Quality": this.selfie_quality_level,
          "Comparison Quality": this.comparison_quality_level,
        };
      }

      if (this.export_options.document_authentication) {
        data.DocumentAuthentication = {
          "Overall Status": this.document_auth_text,
          "Document Type": this.document_description,
          "Issuing Country": this.issuing_country,
          "Document Year": this.document_year,
          "Pages Count": this.pages_count,
          "Doc Type Check": this.auth_check_text("docType"),
          "Expiry Check": this.auth_check_text("expiry"),
          "Image Quality Check": this.auth_check_text("imageQA"),
          "MRZ Check": this.auth_check_text("mrz"),
          "Security Check": this.auth_check_text("security"),
          "Text Check": this.auth_check_text("text"),
        };
      }

      if (this.export_options.raw_json) {
        data.RawJSON = this.safe_ocr_liveness_data;
      }

      return data;
    },

    download_file(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  },
};
